<script lang="ts">
  import { Camas, type Cama } from '../lib/api';
  import { isPilotMode } from '../lib/pilot';
  import { isDemoTenant } from '../lib/clinic-preview.svelte';
  import { session } from '../lib/auth.svelte';

  const esDemo = $derived(isPilotMode() || isDemoTenant(session.value?.tenantId));

  let items = $state<Cama[]>([]);
  let loading = $state(false);
  let error = $state<string | null>(null);
  let info = $state<string | null>(null);

  let nueva = $state<Cama>({ numero: '', servicio: '', estado: 'DISPONIBLE', esUci: false });

  async function cargar() {
    loading = true; error = null;
    try { items = await Camas.list(); }
    catch (e: any) { error = e?.message ?? String(e); }
    finally { loading = false; }
  }

  async function cargarDemo() {
    loading = true; error = null; info = null;
    try {
      const r = await Camas.seedDemo();
      info = `Demo: ${r.camasInsertadas} camas nuevas, ${r.ingresosInsertados} ingresos, ${r.totalCamas} total (${r.tasaOcupacionPct}% ocupación).`;
      await cargar();
    } catch (e: any) { error = e?.message ?? String(e); }
    finally { loading = false; }
  }

  async function crear(e: SubmitEvent) {
    e.preventDefault();
    try {
      await Camas.create({ ...nueva });
      nueva = { numero: '', servicio: '', estado: 'DISPONIBLE', esUci: false };
      await cargar();
    } catch (e: any) { error = e?.message ?? String(e); }
  }

  async function cambiar(c: Cama, estado: string) {
    try { await Camas.estado(c.id!, estado); await cargar(); }
    catch (e: any) { error = e?.message ?? String(e); }
  }

  $effect(() => { cargar(); });
</script>

<section>
  <header>
    <h2>Camas</h2>
    <div class="actions">
      {#if esDemo}<button type="button" class="seed" onclick={cargarDemo} disabled={loading}>Cargar datos demo</button>{/if}
      <button onclick={cargar} disabled={loading}>{loading ? '…' : 'Recargar'}</button>
    </div>
  </header>

  {#if error}<p class="err">⚠ {error}</p>{/if}
  {#if info}<p class="ok">{info}</p>{/if}

  <form onsubmit={crear} class="row">
    <input placeholder="Número (C-501)" bind:value={nueva.numero} required />
    <input placeholder="Servicio" bind:value={nueva.servicio} />
    <select bind:value={nueva.estado}>
      <option>DISPONIBLE</option><option>OCUPADA</option><option>SUCIA</option><option>LIMPIA</option>
      <option>MANTENIMIENTO</option><option>AISLAMIENTO</option><option>BLOQUEADA</option><option>ASIGNADA</option>
    </select>
    <label class="chk"><input type="checkbox" bind:checked={nueva.esUci} /> UCI</label>
    <button type="submit">Agregar</button>
  </form>

  <table>
    <thead><tr><th>Número</th><th>Servicio</th><th>Estado</th><th>UCI</th><th>Acciones</th></tr></thead>
    <tbody>
      {#each items as c (c.id)}
        <tr>
          <td>{c.numero}</td>
          <td>{c.servicio ?? '—'}</td>
          <td><span class="badge {c.estado.toLowerCase()}">{c.estado}</span></td>
          <td>{c.esUci ? 'Sí' : ''}</td>
          <td>
            {#if c.estado !== 'DISPONIBLE'}<button onclick={() => cambiar(c, 'DISPONIBLE')}>→ Disponible</button>{/if}
            {#if c.estado !== 'SUCIA'}<button onclick={() => cambiar(c, 'SUCIA')}>→ Sucia</button>{/if}
            {#if c.estado !== 'LIMPIA'}<button onclick={() => cambiar(c, 'LIMPIA')}>→ Limpia</button>{/if}
            {#if c.estado !== 'MANTENIMIENTO'}<button onclick={() => cambiar(c, 'MANTENIMIENTO')}>→ Mant.</button>{/if}
          </td>
        </tr>
      {:else}
        <tr><td colspan="5" class="empty">No hay camas registradas.</td></tr>
      {/each}
    </tbody>
  </table>
</section>

<style>
  header { display: flex; justify-content: space-between; align-items: center; gap: 1rem; flex-wrap: wrap; }
  .actions { display: flex; gap: .5rem; flex-wrap: wrap; }
  .seed { background: #198754; color: #fff; border: 0; border-radius: 8px; padding: .45rem .75rem; cursor: pointer; font: inherit; }
  .seed:disabled { opacity: .6; cursor: not-allowed; }
  .ok { color: #0a6; margin: .5rem 0; }
  .row { display: flex; gap: .5rem; flex-wrap: wrap; margin: 1rem 0; align-items: center; }
  .row input, .row select, .row button { padding: .45rem .6rem; border-radius: 8px; border: 1px solid #ccc; font: inherit; }
  .row button { background: #0d6efd; color: #fff; border: 0; cursor: pointer; }
  .chk { display: flex; align-items: center; gap: .25rem; font-size: .9rem; }
  table { width: 100%; border-collapse: collapse; }
  th, td { padding: .55rem .6rem; border-bottom: 1px solid #eee; text-align: left; font-size: .92rem; }
  .badge { padding: .15rem .55rem; border-radius: 999px; font-size: .75rem; font-weight: 600; background: #eef; }
  .badge.disponible { background: #d6f5d6; color: #1a6b1a; }
  .badge.ocupada { background: #fde2e2; color: #a02020; }
  .badge.sucia { background: #fff4cc; color: #8a6a00; }
  .badge.limpia { background: #d8f3f0; color: #0a6b63; }
  .badge.mantenimiento { background: #e5e5e5; color: #555; }
  .badge.aislamiento { background: #ede0ff; color: #5b2ea6; }
  .badge.bloqueada { background: #d9dde2; color: #3a4250; }
  .badge.asignada { background: #dce7ff; color: #1e4bb8; }
  .err { color: #b00020; }
  .empty { color: #888; text-align: center; font-style: italic; }
  td button { margin-right: .25rem; padding: .25rem .5rem; border-radius: 6px; border: 1px solid #ccc; background: #fff; cursor: pointer; font-size: .8rem; }
</style>
